import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import FormHelperText from '@mui/material/FormHelperText';
import InputBase, { inputBaseClasses } from '@mui/material/InputBase';
import { alpha, type SxProps, styled, type Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Iconify } from './iconify.js';

const NumberInputRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  overflow: 'hidden',
  borderRadius: `${theme.shape.borderRadius}px`,
  border: 'solid 1px var(--number-input-border)',
}));

const InputContainer = styled('div')({
  display: 'flex',
  flexGrow: 1,
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: 'var(--number-input-background)',
  borderLeft: 'solid 1px var(--number-input-divider)',
  borderRight: 'solid 1px var(--number-input-divider)',
});

const CenteredInput = styled(InputBase)(({ theme }) => ({
  width: '100%',
  [`& .${inputBaseClasses.input}`]: {
    ...theme.typography.body2,
    minHeight: 24,
    textAlign: 'center',
    padding: theme.spacing(0.5, 0),
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const CounterButton = styled(ButtonBase)({
  width: 40,
  flexShrink: 0,
  '&.Mui-disabled': { opacity: 0.48, pointerEvents: 'none' },
});

const CaptionText = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  width: '100%',
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: theme.spacing(-0.25),
  color: theme.palette.text.disabled,
  padding: theme.spacing(0, 0.5, 0.5, 0.5),
}));

export type NumberInputProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  captionText?: ReactNode;
  decrementLabel: string;
  incrementLabel: string;
  inputLabel: string;
  sx?: SxProps<Theme>;
};

/**
 * Clamped into `[min, max]` on every path — the buttons and the keyboard alike
 * — so the value a caller reads is always one it can act on. Empty or
 * unparseable text reads as `min` rather than as zero, because zero of
 * something is not an amount anyone meant to ask for.
 */
export function clampNumberInput(raw: string, bounds: { min: number; max: number }): number {
  const trimmed = raw.trim();
  if (trimmed === '') return bounds.min;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return bounds.min;
  return Math.min(Math.max(Math.trunc(parsed), bounds.min), bounds.max);
}

export function NumberInput({
  value,
  onChange,
  min = 0,
  max = 9999,
  step = 1,
  disabled = false,
  error = false,
  helperText,
  captionText,
  decrementLabel,
  incrementLabel,
  inputLabel,
  sx,
}: NumberInputProps) {
  const inputId = useId();
  const decrementDisabled = disabled || value <= min;
  const incrementDisabled = disabled || value >= max;

  // The field is a draft of its own while the caller still holds the clamped
  // number: clearing it to type a fresh value must not snap the text back to
  // `min` before the next keystroke lands, which is what a fully controlled
  // `value={String(value)}` did. `lastCommitted` is what this component itself
  // last told the caller, so the effect below only pulls the draft back in
  // line with a change the *caller* made — a `value` prop set from outside,
  // such as the +/- buttons of a sibling instance or a reset — and never
  // overwrites what is still being typed with the clamp its own `onChange`
  // just produced.
  const [draft, setDraft] = useState(() => String(value));
  const lastCommitted = useRef(value);

  useEffect(() => {
    if (value === lastCommitted.current) return;
    lastCommitted.current = value;
    setDraft(String(value));
  }, [value]);

  const commit = useCallback(
    (next: number) => {
      lastCommitted.current = next;
      onChange(next);
    },
    [onChange],
  );

  const handleChange = useCallback(
    (raw: string) => {
      setDraft(raw);
      const trimmed = raw.trim();
      if (trimmed === '' || !Number.isFinite(Number(trimmed))) return;
      commit(clampNumberInput(raw, { min, max }));
    },
    [commit, max, min],
  );

  const handleBlur = useCallback(() => {
    const clamped = clampNumberInput(draft, { min, max });
    setDraft(String(clamped));
    commit(clamped);
  }, [commit, draft, max, min]);

  const bump = useCallback(
    (next: number) => {
      setDraft(String(next));
      commit(next);
    },
    [commit],
  );

  return (
    <Box sx={sx}>
      <NumberInputRoot
        sx={(theme) => ({
          '--number-input-border': alpha(theme.palette.grey[500], 0.2),
          '--number-input-divider': alpha(theme.palette.grey[500], 0.2),
          '--number-input-background': error
            ? alpha(theme.palette.error.main, 0.08)
            : alpha(theme.palette.grey[500], 0.08),
        })}
      >
        <CounterButton
          aria-label={decrementLabel}
          disabled={decrementDisabled}
          className={decrementDisabled ? 'Mui-disabled' : undefined}
          onClick={() => bump(Math.max(value - step, min))}
        >
          <Iconify icon="mingcute:minimize-line" width={16} />
        </CounterButton>

        <InputContainer>
          <CenteredInput
            id={inputId}
            inputProps={{ 'aria-label': inputLabel, inputMode: 'numeric' }}
            disabled={disabled}
            value={draft}
            onChange={(event) => handleChange(event.target.value)}
            onBlur={handleBlur}
          />
          {captionText && <CaptionText>{captionText}</CaptionText>}
        </InputContainer>

        <CounterButton
          aria-label={incrementLabel}
          disabled={incrementDisabled}
          className={incrementDisabled ? 'Mui-disabled' : undefined}
          onClick={() => bump(Math.min(value + step, max))}
        >
          <Iconify icon="mingcute:add-line" width={16} />
        </CounterButton>
      </NumberInputRoot>

      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </Box>
  );
}
